import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, CircularProgress, Alert,
  List, ListItem, ListItemIcon, ListItemText, Chip,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import StorageIcon from '@mui/icons-material/Storage';
import PeopleIcon from '@mui/icons-material/People';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { format } from 'date-fns';
import { dmsApi } from '../services/dmsApi';
import useDmsPalette from '../hooks/useDmsPalette';

const VAULT_COLORS = ['#1565C0', '#00C853', '#FFD600', '#FF5722', '#9C27B0', '#00BCD4', '#FF9800', '#E91E63', '#607D8B'];

function StatCard({ icon, label, value, sub }) {
  const MP = useDmsPalette();
  return (
    <Card>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: `${MP.blue}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700}>{value}</Typography>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          {sub && <Typography variant="caption" sx={{ display: 'block', color: MP.green, fontSize: '0.68rem' }}>{sub}</Typography>}
        </Box>
      </CardContent>
    </Card>
  );
}

function HorizontalBar({ label, value, maxValue, color }) {
  const MP = useDmsPalette();
  const pct = maxValue > 0 ? (value / maxValue) * 100 : 0;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.2 }}>
      <Typography variant="body2" sx={{ minWidth: 100, textTransform: 'capitalize', fontSize: '0.82rem' }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, height: 20, bgcolor: MP.black, borderRadius: 1, overflow: 'hidden' }}>
        <Box sx={{ width: `${Math.max(pct, 2)}%`, height: '100%', bgcolor: color, borderRadius: 1, transition: 'width 0.5s ease' }} />
      </Box>
      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 40, textAlign: 'right', fontSize: '0.82rem' }}>
        {value}
      </Typography>
    </Box>
  );
}

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let val = bytes;
  while (val >= 1024 && i < units.length - 1) { val /= 1024; i++; }
  return `${val.toFixed(1)} ${units[i]}`;
}

export default function AdminAnalytics() {
  const MP = useDmsPalette();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await dmsApi.getAnalytics();
        setData(res);
      } catch (err) {
        setError(err.error || err.message || 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const totalDocs = data.totalDocuments ?? data.total ?? 0;
  const totalStorage = data.totalStorage ?? data.storageUsed ?? 0;
  const activeUsers = data.activeUsers ?? 0;
  const activeWorkflows = data.activeWorkflows ?? 0;

  const byVault = data.byVault || data.documentsByVault || [];
  const byStatus = data.byStatus || data.documentsByStatus || [];
  const mostAccessed = data.mostAccessed || data.topDocuments || [];
  const recentActivity = data.recentActivity || data.recentAudit || [];

  const maxVault = Math.max(...byVault.map(v => v.count || 0), 1);
  const maxStatus = Math.max(...byStatus.map(s => s.count || 0), 1);

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }}>Analytics</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Document management system overview
      </Typography>

      {/* Stats cards */}
      <Grid container spacing={2} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<DescriptionIcon sx={{ color: MP.blue }} />} label="Total Documents" value={totalDocs.toLocaleString()} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<StorageIcon sx={{ color: MP.green }} />} label="Total Storage" value={formatBytes(totalStorage)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<PeopleIcon sx={{ color: MP.yellow }} />} label="Active Users" value={activeUsers} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<AccountTreeIcon sx={{ color: '#9C27B0' }} />} label="Active Workflows" value={activeWorkflows} />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Documents by Vault */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>Documents by Vault</Typography>
              {byVault.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No data</Typography>
              ) : (
                byVault.map((v, i) => (
                  <HorizontalBar
                    key={v._id || v.vault || i}
                    label={v._id || v.vault || v.name || 'Unknown'}
                    value={v.count || 0}
                    maxValue={maxVault}
                    color={VAULT_COLORS[i % VAULT_COLORS.length]}
                  />
                ))
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Documents by Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>Documents by Status</Typography>
              {byStatus.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No data</Typography>
              ) : (
                byStatus.map((s, i) => (
                  <HorizontalBar
                    key={s._id || s.status || i}
                    label={s._id || s.status || 'Unknown'}
                    value={s.count || 0}
                    maxValue={maxStatus}
                    color={VAULT_COLORS[(i + 3) % VAULT_COLORS.length]}
                  />
                ))
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Most Accessed Documents */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={1.5}>Most Accessed Documents</Typography>
              {mostAccessed.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No data</Typography>
              ) : (
                <List dense disablePadding>
                  {mostAccessed.slice(0, 10).map((doc, idx) => (
                    <ListItem key={doc._id || idx} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <InsertDriveFileIcon fontSize="small" sx={{ color: MP.blue }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={doc.title || doc.name || 'Untitled'}
                        primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: 500, noWrap: true }}
                        secondary={doc.vault}
                        secondaryTypographyProps={{ fontSize: '0.7rem' }}
                      />
                      <Chip label={`${doc.accessCount || doc.views || 0} views`} size="small" sx={{ fontSize: '0.68rem', height: 20, bgcolor: MP.black }} />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={1.5}>Recent Activity</Typography>
              {recentActivity.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No recent activity</Typography>
              ) : (
                recentActivity.slice(0, 20).map((entry, idx) => (
                  <Box key={entry._id || idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1, pb: 1, borderBottom: idx < recentActivity.length - 1 ? `1px solid ${MP.border}` : 'none' }}>
                    <Box sx={{
                      width: 6, height: 6, borderRadius: '50%', mt: 0.8, flexShrink: 0,
                      bgcolor: entry.action === 'upload' ? MP.green : entry.action === 'delete' ? '#ef4444' : MP.blue,
                    }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" sx={{ fontSize: '0.76rem', display: 'block' }}>
                        <strong>{entry.userName || entry.userId || 'User'}</strong> {entry.action}{entry.action?.endsWith('e') ? 'd' : 'ed'}{' '}
                        <em>{entry.documentTitle || 'a document'}</em>
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                        {entry.timestamp || entry.createdAt ? format(new Date(entry.timestamp || entry.createdAt), 'MMM d, HH:mm') : ''}
                      </Typography>
                    </Box>
                  </Box>
                ))
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
